import { onBeforeUnmount, onMounted } from 'vue';

export type KeyHandler = (event: KeyboardEvent) => void;

export type KeyHandlers = Record<string, KeyHandler>;

export interface UseKeydownReturn {
  handleKeydown: (event: KeyboardEvent) => void;
}

/**
 * Composable for handling keyboard events
 *
 * @param keyHandlers - Object mapping key names to handler functions
 * @returns Object containing the handleKeydown function
 *
 * @example
 * const { handleKeydown } = useKeydown({
 *   'Enter': (event) => console.log('Enter pressed'),
 *   'Escape': (event) => console.log('Escape pressed'),
 * });
 */
export function useKeydown(keyHandlers: KeyHandlers): UseKeydownReturn {
  const handleKeydown = (event: KeyboardEvent): void => {
    const handler = keyHandlers[event.key];
    if (handler) {
      handler(event);
    }
  };

  onMounted(() => {
    window.addEventListener('keydown', handleKeydown);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeydown);
  });

  return {
    handleKeydown,
  };
}
